import React from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';

import { MERIDIAN_COLORS } from '../../../Util';
import {
  ChatMessageType,
  ChatParticipantStateChangeType,
} from '../../../api/ContactListening/schema';
import { FormattedMessage, useIntl } from 'react-intl';

export interface ChatMessageBoxProps {
  message?: string;
  translatedMessage?: string;
  isCustomer?: boolean;
  chatType: ChatMessageType;
  stateType?: ChatParticipantStateChangeType;
  timestamp?: string;
  timezone?: string;
}

export const ChatMessageBox: React.FC<ChatMessageBoxProps> = ({
  message,
  translatedMessage,
  isCustomer,
  chatType,
  stateType,
  timestamp,
  timezone,
}) => {
  const intl = useIntl();
  if (chatType === ChatMessageType.MESSAGE) {
    if (!message || message === '') {
      return <></>;
    }
    let timestampStr;
    if (timestamp && timezone) {
      const safeTimestamp = Date.parse(timestamp);
      if (!isNaN(safeTimestamp)) {
        timestampStr = new Date(safeTimestamp).toLocaleTimeString(timezone);
      }
    }
    return (
      <Column
        width='100%'
        alignmentHorizontal={isCustomer ? 'start' : 'end'}
        spacing='100'
      >
        <Row width='90%' alignmentHorizontal={isCustomer ? 'start' : 'end'}>
          <Box
            type={isCustomer ? 'outline' : 'fill'}
            spacingInset='200'
            backgroundColor={
              isCustomer
                ? MERIDIAN_COLORS.MERIDIAN_COLOR_LIGHT_GRAY
                : MERIDIAN_COLORS.MERIDIAN_COLOR_BLUE_DARK
            }
          >
            <Text type='b300' color={isCustomer ? 'primary' : 'inverted'}>
              {message}
            </Text>
          </Box>
        </Row>
        {translatedMessage && translatedMessage !== message && (
          <Row width='90%' alignmentHorizontal={isCustomer ? 'start' : 'end'}>
            <Box
              type={isCustomer ? 'outline' : 'fill'}
              spacingInset='200'
              backgroundColor={
                isCustomer
                  ? MERIDIAN_COLORS.MERIDIAN_COLOR_LIGHT_GRAY
                  : MERIDIAN_COLORS.MERIDIAN_COLOR_BLUE_DARK
              }
            >
              <Text type='b300' color={isCustomer ? 'primary' : 'inverted'}>
                {translatedMessage}
              </Text>
            </Box>
          </Row>
        )}
        {timestampStr && (
          <Text type='b200' color='secondary'>
            {timestampStr}
          </Text>
        )}
      </Column>
    );
  } else if (
    chatType === ChatMessageType.CHAT_PARKED ||
    chatType === ChatMessageType.CHAT_TRANSFERRED
  ) {
    return (
      <Column width='100%' alignmentHorizontal='center'>
        <Text alignment='center' type='b300'>
          {chatType === ChatMessageType.CHAT_PARKED ? (
            <FormattedMessage
              id='cs_perfportal_contact_listening_chat_history_chat_parked'
              defaultMessage='chat parked'
            />
          ) : (
            <FormattedMessage
              id='cs_perfportal_contact_listening_chat_history_chat_transferred'
              defaultMessage='chat transferred'
            />
          )}
        </Text>
        <br />
        {message && (
          <Text alignment='center' type='b300'>
            {intl.formatMessage({
              id: 'cs_perfportal_contact_listening_chat_history_notes',
              defaultMessage: 'notes: ',
              description: 'notes: ',
            }) + message}
          </Text>
        )}
      </Column>
    );
  } else {
    return (
      <Column width='100%' alignmentHorizontal='center'>
        <Text alignment='center' type='b300' color='secondary'>
          {stateType === ChatParticipantStateChangeType.CONNECTED && (
            <FormattedMessage
              id='cs_perfportal_contact_listening_chat_history_agent_connected'
              defaultMessage='agent connected'
            />
          )}
          {stateType === ChatParticipantStateChangeType.ON_HOLD && (
            <FormattedMessage
              id='cs_perfportal_contact_listening_chat_history_participant_on_hold'
              defaultMessage='participant on hold'
            />
          )}
          {stateType === ChatParticipantStateChangeType.DISCONNECTED && (
            <FormattedMessage
              id='cs_perfportal_contact_listening_chat_history_chat_disconnected'
              defaultMessage='chat disconnected'
            />
          )}
          {stateType === ChatParticipantStateChangeType.OFF_HOLD && (
            <FormattedMessage
              id='cs_perfportal_contact_listening_chat_history_participant_off_hold'
              defaultMessage='participant off hold'
            />
          )}
        </Text>
        {message && <Text type='b300'>{message}</Text>}
      </Column>
    );
  }
};
export default ChatMessageBox;
